package pl.nickers.match3.ui

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.selection.toggleable
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Redo
import androidx.compose.material.icons.automirrored.filled.Undo
import androidx.compose.material.icons.filled.FolderOpen
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.HideImage
import androidx.compose.material.icons.filled.RestartAlt
import androidx.compose.material.icons.filled.Save
import androidx.compose.material.icons.filled.GpsFixed
import androidx.compose.material.icons.filled.ViewStream
import androidx.compose.material.icons.filled.Wallpaper
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.IconButtonColors
import androidx.compose.material3.IconButtonDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shadow
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlinproject.composeapp.generated.resources.Res
import kotlinproject.composeapp.generated.resources.bomb
import kotlinproject.composeapp.generated.resources.button_round
import kotlinproject.composeapp.generated.resources.button_round_pressed
import org.jetbrains.compose.resources.painterResource
import pl.nickers.match3.GoalInfo
import pl.nickers.match3.GridPos
import pl.nickers.match3.MAX_BOARD_DIMENSION
import pl.nickers.match3.MIN_BOARD_DIMENSION
import pl.nickers.match3.InputMode
import pl.nickers.match3.MoveLimitInfo
import pl.nickers.match3.PowerUpInfo
import pl.nickers.match3.ecs.PowerUpType

@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun GameBottomToolbar(
    enabled: Boolean,
    selectedPos: GridPos?,
    onEditEntity: () -> Unit,
    canUndoMove: Boolean,
    canRedoMove: Boolean,
    disableNewJellySpawn: Boolean,
    hasBackgroundImage: Boolean,
    onResetBoard: () -> Unit,
    onDisableNewJellySpawnChange: (Boolean) -> Unit,
    onSave: () -> Unit,
    onLoad: () -> Unit,
    onUndoMove: () -> Unit,
    onRedoMove: () -> Unit,
    onSetBackground: () -> Unit,
    onRemoveBackground: () -> Unit,
    onToggleDebugOverlay: () -> Unit,
    isDebugOverlayVisible: Boolean,
    onAddPowerUpUse: (PowerUpType) -> Unit,
    boardWidth: Int,
    boardHeight: Int,
    onSetBoardSize: (Int, Int) -> Unit,
    modifier: Modifier = Modifier,
) {
    var showResetConfirm by remember { mutableStateOf(false) }
    if (showResetConfirm) {
        AlertDialog(
            onDismissRequest = { showResetConfirm = false },
            title = { Text("Reset board?") },
            text = {
                Text(
                    "This replaces the board with a new random layout and clears your score. " +
                        "Move history will be cleared.",
                )
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        showResetConfirm = false
                        onResetBoard()
                    },
                ) {
                    Text("Reset")
                }
            },
            dismissButton = {
                TextButton(onClick = { showResetConfirm = false }) {
                    Text("Cancel")
                }
            },
        )
    }
    val barColor = MaterialTheme.colorScheme.surface.copy(alpha = 0.94f)
    val iconTint = MaterialTheme.colorScheme.onSurface
    val iconButtonColors = IconButtonDefaults.iconButtonColors(
        contentColor = iconTint,
        disabledContentColor = iconTint.copy(alpha = 0.38f),
    )
    Surface(
        modifier = modifier
            .padding(horizontal = 20.dp, vertical = 12.dp)
            .fillMaxWidth(0.92f),
        shape = RoundedCornerShape(28.dp),
        color = barColor,
        shadowElevation = 8.dp,
        tonalElevation = 2.dp,
    ) {
        FlowRow(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 8.dp, vertical = 4.dp),
            horizontalArrangement = Arrangement.spacedBy(4.dp, Alignment.CenterHorizontally),
            verticalArrangement = Arrangement.Center,
            itemVerticalAlignment = Alignment.CenterVertically,
        ) {
            Row(
                modifier = Modifier
                    .padding(horizontal = 8.dp, vertical = 2.dp)
                    .toggleable(
                        value = disableNewJellySpawn,
                        enabled = enabled,
                        role = Role.Checkbox,
                        onValueChange = onDisableNewJellySpawnChange,
                    ),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Checkbox(
                    checked = disableNewJellySpawn,
                    onCheckedChange = null,
                    enabled = enabled,
                )
                Text(
                    text = "Disable new jellies after clears",
                    color = iconTint,
                    fontSize = 13.sp,
                )
            }
            ToolbarIconButton(
                tooltipText = "Reset board — new random layout",
                onClick = { showResetConfirm = true },
                enabled = enabled,
                iconButtonColors = iconButtonColors,
            ) {
                Icon(
                    imageVector = Icons.Filled.RestartAlt,
                    contentDescription = "Reset board",
                    modifier = Modifier.size(26.dp),
                )
            }
            BoardSizeStepper(
                label = "W",
                value = boardWidth,
                enabled = enabled,
                iconTint = iconTint,
                onDecrement = { onSetBoardSize(boardWidth - 1, boardHeight) },
                onIncrement = { onSetBoardSize(boardWidth + 1, boardHeight) },
            )
            BoardSizeStepper(
                label = "H",
                value = boardHeight,
                enabled = enabled,
                iconTint = iconTint,
                onDecrement = { onSetBoardSize(boardWidth, boardHeight - 1) },
                onIncrement = { onSetBoardSize(boardWidth, boardHeight + 1) },
            )
            ToolbarIconButton(
                tooltipText = "Save game to file",
                onClick = onSave,
                enabled = enabled,
                iconButtonColors = iconButtonColors,
            ) {
                Icon(
                    imageVector = Icons.Filled.Save,
                    contentDescription = "Save game",
                    modifier = Modifier.size(26.dp),
                )
            }
            ToolbarIconButton(
                tooltipText = "Load game from file",
                onClick = onLoad,
                enabled = enabled,
                iconButtonColors = iconButtonColors,
            ) {
                Icon(
                    imageVector = Icons.Filled.FolderOpen,
                    contentDescription = "Load game",
                    modifier = Modifier.size(26.dp),
                )
            }
            ToolbarIconButton(
                tooltipText = if (hasBackgroundImage) "Change background image" else "Set background image",
                onClick = onSetBackground,
                enabled = enabled,
                iconButtonColors = iconButtonColors,
            ) {
                Icon(
                    imageVector = Icons.Filled.Wallpaper,
                    contentDescription = if (hasBackgroundImage) "Change background" else "Set background",
                    modifier = Modifier.size(26.dp),
                )
            }
            if (hasBackgroundImage) {
                ToolbarIconButton(
                    tooltipText = "Remove background image",
                    onClick = onRemoveBackground,
                    enabled = enabled,
                    iconButtonColors = iconButtonColors,
                ) {
                    Icon(
                        imageVector = Icons.Filled.HideImage,
                        contentDescription = "Remove background",
                        modifier = Modifier.size(26.dp),
                    )
                }
            }
            ToolbarIconButton(
                tooltipText = "Back — undo last move",
                onClick = onUndoMove,
                enabled = enabled && canUndoMove,
                iconButtonColors = iconButtonColors,
            ) {
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.Undo,
                    contentDescription = "Undo move",
                    modifier = Modifier.size(26.dp),
                )
            }
            ToolbarIconButton(
                tooltipText = "Forward — redo move",
                onClick = onRedoMove,
                enabled = enabled && canRedoMove,
                iconButtonColors = iconButtonColors,
            ) {
                Icon(
                    imageVector = Icons.AutoMirrored.Filled.Redo,
                    contentDescription = "Redo move",
                    modifier = Modifier.size(26.dp),
                )
            }
            ToolbarIconButton(
                tooltipText = "Edit selected cell",
                onClick = onEditEntity,
                enabled = enabled && selectedPos != null,
                iconButtonColors = iconButtonColors,
            ) {
                Icon(
                    imageVector = Icons.Filled.Edit,
                    contentDescription = "Edit entity",
                    modifier = Modifier.size(26.dp),
                )
            }
            Row(
                modifier = Modifier
                    .padding(horizontal = 8.dp, vertical = 2.dp)
                    .toggleable(
                        value = isDebugOverlayVisible,
                        enabled = true,
                        role = Role.Checkbox,
                        onValueChange = { onToggleDebugOverlay() },
                    ),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Checkbox(
                    checked = isDebugOverlayVisible,
                    onCheckedChange = null,
                )
                Text(
                    text = "DBG overlay",
                    color = iconTint,
                    fontSize = 13.sp,
                )
            }
            Row(
                modifier = Modifier.padding(horizontal = 4.dp, vertical = 2.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                Text(text = "+1:", color = iconTint, fontSize = 13.sp)
                ToolbarIconButton(
                    tooltipText = "+1 Bomb use",
                    onClick = { onAddPowerUpUse(PowerUpType.BOMB) },
                    enabled = enabled,
                    iconButtonColors = iconButtonColors,
                ) {
                    Image(
                        painter = painterResource(Res.drawable.bomb),
                        contentDescription = "+1 Bomb",
                        modifier = Modifier.size(24.dp),
                    )
                }
                ToolbarIconButton(
                    tooltipText = "+1 Row Bomb use",
                    onClick = { onAddPowerUpUse(PowerUpType.ROW_BOMB) },
                    enabled = enabled,
                    iconButtonColors = iconButtonColors,
                ) {
                    Icon(
                        imageVector = Icons.Filled.ViewStream,
                        contentDescription = "+1 Row Bomb",
                        modifier = Modifier.size(24.dp),
                    )
                }
                ToolbarIconButton(
                    tooltipText = "+1 Targeted Remove use",
                    onClick = { onAddPowerUpUse(PowerUpType.TARGETED_REMOVE) },
                    enabled = enabled,
                    iconButtonColors = iconButtonColors,
                ) {
                    Icon(
                        imageVector = Icons.Filled.GpsFixed,
                        contentDescription = "+1 Targeted Remove",
                        modifier = Modifier.size(24.dp),
                    )
                }
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun ToolbarIconButton(
    tooltipText: String,
    onClick: () -> Unit,
    enabled: Boolean,
    iconButtonColors: IconButtonColors,
    icon: @Composable () -> Unit,
) {
    val tooltipState = rememberTooltipState(isPersistent = false)
    TooltipBox(
        positionProvider = TooltipDefaults.rememberTooltipPositionProvider(
            positioning = TooltipAnchorPosition.Above,
        ),
        tooltip = {
            PlainTooltip {
                Text(tooltipText)
            }
        },
        state = tooltipState,
    ) {
        IconButton(
            onClick = onClick,
            enabled = enabled,
            colors = iconButtonColors,
            modifier = Modifier.size(48.dp),
        ) {
            icon()
        }
    }
}

/**
 * Compact dev-tools control for editing one board dimension (width or height).
 * Shows the current value flanked by −/+ buttons.
 */
@Composable
private fun BoardSizeStepper(
    label: String,
    value: Int,
    enabled: Boolean,
    iconTint: Color,
    onDecrement: () -> Unit,
    onIncrement: () -> Unit,
) {
    Row(
        modifier = Modifier.padding(horizontal = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        TextButton(
            onClick = onDecrement,
            enabled = enabled && value > MIN_BOARD_DIMENSION,
            contentPadding = PaddingValues(horizontal = 8.dp),
        ) { Text("−", fontSize = 18.sp) }
        Text(
            text = "$label $value",
            color = iconTint,
            fontSize = 13.sp,
        )
        TextButton(
            onClick = onIncrement,
            enabled = enabled && value < MAX_BOARD_DIMENSION,
            contentPadding = PaddingValues(horizontal = 8.dp),
        ) { Text("+", fontSize = 18.sp) }
    }
}

@Composable
internal fun GoalsRow(
    goals: List<GoalInfo>,
    moveLimit: MoveLimitInfo? = null,
    modifier: Modifier = Modifier,
    flightAnchors: FlightAnchorRegistry? = null,
) {
    if (goals.isEmpty() && moveLimit == null) return
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(16.dp, Alignment.CenterHorizontally),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        if (goals.isNotEmpty()) {
            Text(
                text = "🧪 Brewing",
                color = GrimoirePalette.Ink,
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
        if (moveLimit != null) {
            MoveLimitChip(moveLimit = moveLimit)
        }
        goals.forEach { goal ->
            val alpha = if (goal.completed) 0.5f else 1.0f
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier
                    .background(
                        GrimoirePalette.ChipInk.copy(alpha = GrimoirePalette.ChipInk.alpha * alpha),
                        shape = RoundedCornerShape(8.dp),
                    )
                    .padding(horizontal = 12.dp, vertical = 6.dp)
                    .then(
                        if (goal.completed) Modifier.border(2.dp, GrimoirePalette.Gold, RoundedCornerShape(8.dp))
                        else Modifier
                    ),
            ) {
                Box(
                    modifier = Modifier
                        .size(36.dp)
                        .then(
                            if (flightAnchors != null) {
                                Modifier.onGloballyPositioned { coords ->
                                    flightAnchors.goalIcons[goal.pieceId] = coords
                                }
                            } else Modifier,
                        ),
                ) {
                    Image(
                        painter = painterResource(goal.bodyImage.toBodyDrawableRes()),
                        contentDescription = "Goal jelly",
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Fit,
                        alpha = alpha,
                    )
                }
                Text(
                    text = if (goal.completed) "Done" else "x${goal.remainingQuantity}",
                    color = if (goal.completed) GrimoirePalette.Gold else GrimoirePalette.Cream,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center,
                )
            }
        }
    }
}
@Composable
private fun MoveLimitChip(moveLimit: MoveLimitInfo) {
    val depleted = moveLimit.remaining <= 0
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier
            .background(
                GrimoirePalette.ChipInk,
                shape = RoundedCornerShape(8.dp),
            )
            .padding(horizontal = 12.dp, vertical = 6.dp),
    ) {
        Text(
            text = "Moves",
            color = GrimoirePalette.Cream,
            fontSize = 11.sp,
            fontWeight = FontWeight.SemiBold,
        )
        Text(
            text = moveLimit.remaining.coerceAtLeast(0).toString(),
            color = if (depleted) Color(0xFFF08A5C) else GrimoirePalette.Cream,
            fontSize = 22.sp,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Center,
        )
    }
}

@Composable
internal fun PowerUpBar(
    powerUps: List<PowerUpInfo>,
    enabled: Boolean,
    inputMode: InputMode,
    onUseBombPowerUp: () -> Unit,
    onToggleRowBombPowerUp: () -> Unit,
    onToggleTargetedRemovePowerUp: () -> Unit,
    modifier: Modifier = Modifier,
    flightAnchors: FlightAnchorRegistry? = null,
) {
    val rowBombArmed = inputMode == InputMode.ROW_BOMB_TARGETING
    val targetedRemoveArmed = inputMode == InputMode.TARGETED_REMOVE_TARGETING
    Row(
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = 24.dp, vertical = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(12.dp, Alignment.CenterHorizontally),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        powerUps.forEach { powerUp ->
            when (powerUp.type) {
                PowerUpType.BOMB -> {
                    val usesLeft = powerUp.uses
                    val isUsable = enabled && usesLeft > 0 && !rowBombArmed
                    RoundPowerUpButton(
                        onClick = onUseBombPowerUp,
                        enabled = isUsable,
                        armed = false,
                        label = "×$usesLeft",
                        modifier = if (flightAnchors != null) {
                            Modifier.onGloballyPositioned { coords ->
                                flightAnchors.bombButtons[powerUp.entityId] = coords
                            }
                        } else Modifier,
                    ) {
                        Image(
                            painter = painterResource(Res.drawable.bomb),
                            contentDescription = "Bomb power-up",
                            modifier = Modifier.size(30.dp),
                        )
                    }
                }
                PowerUpType.ROW_BOMB -> {
                    val usesLeft = powerUp.uses
                    val isUsable = enabled && (rowBombArmed || usesLeft > 0)
                    RoundPowerUpButton(
                        onClick = onToggleRowBombPowerUp,
                        enabled = isUsable,
                        armed = rowBombArmed,
                        label = if (rowBombArmed) "Tap a row" else "×$usesLeft",
                    ) {
                        Icon(
                            imageVector = Icons.Filled.ViewStream,
                            contentDescription = "Row bomb power-up",
                            modifier = Modifier.size(26.dp),
                            tint = PowerUpInkColor,
                        )
                    }
                }
                PowerUpType.TARGETED_REMOVE -> {
                    val usesLeft = powerUp.uses
                    val isUsable = enabled && (targetedRemoveArmed || usesLeft > 0)
                    RoundPowerUpButton(
                        onClick = onToggleTargetedRemovePowerUp,
                        enabled = isUsable,
                        armed = targetedRemoveArmed,
                        label = if (targetedRemoveArmed) "Tap target" else "×$usesLeft",
                    ) {
                        Icon(
                            imageVector = Icons.Filled.GpsFixed,
                            contentDescription = "Targeted remove power-up",
                            modifier = Modifier.size(26.dp),
                            tint = PowerUpInkColor,
                        )
                    }
                }
            }
        }
    }
}

/** Sepia ink matching the hand-drawn art set. */
private val PowerUpInkColor = GrimoirePalette.Ink

/**
 * A power-up button drawn on the round parchment-medallion background from the
 * grimoire art set. Armed toggles switch to the pressed (darker) medallion and
 * a golden halo; the uses-count label sits below the disc.
 */
@Composable
private fun RoundPowerUpButton(
    onClick: () -> Unit,
    enabled: Boolean,
    armed: Boolean,
    label: String,
    modifier: Modifier = Modifier,
    icon: @Composable () -> Unit,
) {
    Column(
        modifier = modifier.alpha(if (enabled) 1f else 0.45f),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Box(
            modifier = Modifier
                .size(56.dp)
                .then(
                    if (armed) Modifier.border(3.dp, Color(0xFFE8B04C), CircleShape)
                    else Modifier
                )
                .clip(CircleShape)
                .clickable(enabled = enabled, onClick = onClick),
            contentAlignment = Alignment.Center,
        ) {
            Image(
                painter = painterResource(
                    if (armed) Res.drawable.button_round_pressed else Res.drawable.button_round
                ),
                contentDescription = null,
                modifier = Modifier.fillMaxSize(),
                contentScale = ContentScale.Fit,
            )
            icon()
        }
        Text(
            text = label,
            fontWeight = FontWeight.Bold,
            fontSize = 12.sp,
            color = PowerUpInkColor,
        )
    }
}

@Composable
internal fun ScoreDisplay(score: Int, modifier: Modifier = Modifier) {
    Text(
        text = "$score",
        modifier = modifier
            .background(
                GrimoirePalette.ChipInk,
                shape = RoundedCornerShape(12.dp),
            )
            .padding(horizontal = 12.dp, vertical = 6.dp),
        color = GrimoirePalette.Gold,
        fontSize = 20.sp,
        fontWeight = FontWeight.Bold,
        textAlign = TextAlign.End,
        maxLines = 1,
        style = TextStyle(
            shadow = Shadow(
                color = Color.Black.copy(alpha = 0.5f),
                offset = Offset(1f, 1f),
                blurRadius = 3f,
            ),
        ),
    )
}


@Composable
internal fun FamiliarProgressBar(
    progress: Int,
    threshold: Int,
    modifier: Modifier = Modifier,
) {
    val fraction = (progress.toFloat() / threshold).coerceIn(0f, 1f)
    Column(
        modifier = modifier
            .width(48.dp)
            .background(
                GrimoirePalette.ChipInk,
                shape = RoundedCornerShape(8.dp),
            )
            .padding(horizontal = 4.dp, vertical = 4.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Image(
            painter = painterResource(Res.drawable.bomb),
            contentDescription = "Familiar",
            modifier = Modifier.size(24.dp),
            contentScale = ContentScale.Fit,
        )
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(6.dp)
                .background(GrimoirePalette.Cream.copy(alpha = 0.3f), RoundedCornerShape(3.dp)),
        ) {
            Box(
                modifier = Modifier
                    .fillMaxHeight()
                    .fillMaxWidth(fraction)
                    .background(GrimoirePalette.Gold, RoundedCornerShape(3.dp)),
            )
        }
        Text(
            text = "$progress/$threshold",
            color = GrimoirePalette.Cream,
            fontSize = 10.sp,
            fontWeight = FontWeight.Bold,
        )
    }
}
